import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

import { IonicModule, IonModal, ModalController } from '@ionic/angular';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { LoadingService } from '@core/services/loading/loading.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { AccountHideType, AccountVisibilityService } from '@core/services/products/account-visibility.service';

import { Account, Product, ProductTypes } from '@shared/models/products.model';
import { SharedModule } from '@shared/shared.module';

@UntilDestroy()
@Component({
    selector: 'app-restrict-account-modal',
    templateUrl: './restrict-account-modal.component.html',
    styleUrls: ['./restrict-account-modal.component.scss'],
    imports: [CommonModule, IonicModule, SharedModule]
})
export class RestrictAccountModalComponent {
  @Input() modal: IonModal;
  @Input() staticHeight: boolean;
  @Input() product: Product;

  isCompleted = false;
  isSuccess = false;
  errorText?: string;

  constructor(
    private accountVisibilityService: AccountVisibilityService,
    private loadingService: LoadingService,
    private navigationService: NavigationService,
    private modalController: ModalController
  ) {}

  /**
   * Форматирует номер счета для отображения
   */
  get formattedAccountNumber(): string {
    const accountNumber = this.product.number || this.product.id;

    if (!accountNumber || accountNumber.length !== 20) {
      return accountNumber;
    }

    // Форматируем как: 40 817 810 202 050 000 383
    return accountNumber.replace(/(\d{2})(\d{3})(\d{3})(\d{3})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4 $5 $6 $7');
  }

  /**
   * Форматирует дату для отображения
   */
  get formattedOpenDate(): string {
    // Пытаемся получить openDate из Account, если нет - fallback на beginDate из Product
    const openDate = (this.product as Account).openDate || this.product.beginDate;

    if (!openDate) {
      return '';
    }

    const dateObj = typeof openDate === 'string' ? new Date(openDate) : openDate;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return dateObj.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Выполняет жёсткое скрытие счёта
   */
  restrictAccount() {
    this.loadingService.showLoader(true);

    this.accountVisibilityService
      .hideAccount({
        accId: this.product.id,
        hideType: AccountHideType.HARD_HIDE,
        isAccount: this.product.productType !== ProductTypes.CARD
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.loadingService.showLoader(false);
          console.log('Account hard hide successful:', response);
          this.isCompleted = true;
          this.isSuccess = true;
        },
        error: (error) => {
          this.loadingService.showLoader(false);
          console.error('Error restricting account:', error);
          this.isCompleted = true;
          this.isSuccess = false;

          // Task #27007: Обработка ошибки 3011
          if (error?.type === 'FORBIDDEN_CHANGE') {
            this.errorText = error?.userMessage || 'На продукт наложен запрет по изменению ограничений';
          } else if (error?.type === 'ALREADY_HIDDEN') {
            this.errorText = error?.userMessage || 'На продукт уже наложено полное ограничение';
          } else {
            this.errorText = error?.userMessage || error?.errorInfo || error?.errorText || 'Не удалось ограничить счет. Попробуйте позже.';
          }
        }
      });
  }

  goToMain() {
    this.navigationService.setRoot('main').then(() => this.modalController.dismiss({ closed: true }));
  }
}
